/**
 * NOTE: This code belongs to Kangaroo Rewards. Unauthorized use is prohibited
 */

@file:Suppress("unused", "MemberVisibilityCanBePrivate", "EXPERIMENTAL_API_USAGE")
@file:OptIn(ExperimentalJsExport::class)
package features.redeemAmazonCatalogueItem

import kangaroorewards.appsdk.core.api.Api
import kangaroorewards.appsdk.core.domain.Result
import kangaroorewards.appsdk.core.domain.Result.Idle
import kangaroorewards.appsdk.core.domain.Result.Loading
import kangaroorewards.appsdk.core.domain.SerializedResult
import kangaroorewards.appsdk.core.domain.toJsonResult
import kangaroorewards.appsdk.core.utils.CFlow
import kangaroorewards.appsdk.core.utils.asCFlow
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.serialization.InternalSerializationApi
import kotlin.js.ExperimentalJsExport
import features.redeemAmazonCatalogueItem.internal.RedeemAmazonCatalogueItemsUseCase
import features.redeemAmazonCatalogueItem.internal.RedeemAmazonCatalogueItemsUseCase.Params
import features.redeemAmazonCatalogueItem.models.RedeemResponseModel
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import features.redeemAmazonCatalogueItem.models.RedeemAmazonItemRequest

typealias RedeemAmazonCatalogueItemsState = CFlow<RedeemAmazonCatalogueItemsUseCaseResult?>

typealias RedeemAmazonCatalogueItemsUseCaseResult = Result<RedeemResponseModel>

fun RedeemAmazonCatalogueItemsUseCaseResult.serializeRedeemAmazonCatalogueItemsApiResult(): SerializedResult<String> {
    return this.toJsonResult<RedeemResponseModel>()
}

/**
 * Serializes [RedeemAmazonCatalogueItemsState]'s data and returns a new
 * [CFlow] containing data serialized as a JSON [String].
 */
@OptIn(ExperimentalCoroutinesApi::class)
fun RedeemAmazonCatalogueItemsState.serializeRedeemAmazonCatalogueItemsState(): CFlow<SerializedResult<String>?> {
    return this.mapLatest {
        it?.toJsonResult<RedeemResponseModel>()
    }.asCFlow()
}

/**
 * Redeem an Amazon catalogue item.  [Api] class
 */
class RedeemAmazonCatalogueItemsApi : Api() {

    val redeemAmazonCatalogueItemsState: RedeemAmazonCatalogueItemsState
        get() = _redeemAmazonCatalogueItemsState.asCFlow()

    companion object {
        private val _redeemAmazonCatalogueItemsState: MutableStateFlow<RedeemAmazonCatalogueItemsUseCaseResult> = MutableStateFlow(Idle())
    }

    private val redeemAmazonCatalogueItemsUseCase = RedeemAmazonCatalogueItemsUseCase()

    /**
     * Redeem an Amazon catalogue item. 
     */
    suspend fun redeemAmazonCatalogueItem(
        overrideHeaders: Map<String, String>? = null,
        redeemAmazonItemRequest: RedeemAmazonItemRequest
    ): Result<RedeemResponseModel> {
        /* set state to Loading as soon as function is called */
        _redeemAmazonCatalogueItemsState.value = Loading()
        val result = this.redeemAmazonCatalogueItemsUseCase(
            Params(
                overrideHeaders = overrideHeaders,
                redeemAmazonItemRequest = redeemAmazonItemRequest
            )
        ) { result ->
            _redeemAmazonCatalogueItemsState.value = result
            return@redeemAmazonCatalogueItemsUseCase result
        }
        return result
    }

    /**
    * Redeem an Amazon catalogue item. 
    *
    * This method is used by the Native artifact when dealing with object body params
    */
    suspend fun redeemAmazonCatalogueItem(
        overrideHeaders: Map<String, String>? = null,
        methods: Map<String, Any>
    
    ): Result<RedeemResponseModel> {
        return redeemAmazonCatalogueItem(
            overrideHeaders = overrideHeaders,
            redeemAmazonItemRequest = Json.decodeFromString(methods.getValue("redeemAmazonItemRequest") as String)
        )
    }
}
